import { z } from "zod";

// Comprehensive email validation schema
export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .max(255, "Email must be less than 255 characters")
  .email("Please enter a valid email address")
  .refine(
    (email) => {
      // Additional validation beyond basic format
      const parts = email.split("@");
      if (parts.length !== 2) return false;
      
      const [local, domain] = parts;
      
      // Check local part isn't too long (max 64 chars per RFC)
      if (local.length > 64) return false;
      
      // Check domain has at least one dot and valid TLD
      const domainParts = domain.split(".");
      if (domainParts.length < 2) return false;
      
      const tld = domainParts[domainParts.length - 1];
      if (tld.length < 2) return false;
      
      // Check for common typos in popular domains
      const commonDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com"];
      const typoPatterns = [
        { pattern: /^gmai[l]?\.com$/i, suggestion: "gmail.com" },
        { pattern: /^gmial\.com$/i, suggestion: "gmail.com" },
        { pattern: /^gamil\.com$/i, suggestion: "gmail.com" },
        { pattern: /^yaho[o]?\.com$/i, suggestion: "yahoo.com" },
        { pattern: /^hotmai[l]?\.com$/i, suggestion: "hotmail.com" },
      ];
      
      // These are warnings, not failures - return true but log
      for (const typo of typoPatterns) {
        if (typo.pattern.test(domain) && !commonDomains.includes(domain.toLowerCase())) {
          console.warn(`Possible email typo: ${domain} might be ${typo.suggestion}`);
        }
      }
      
      return true;
    },
    { message: "Please check your email address" }
  );

// Simple validation function for inline use
export function isValidEmail(email: string): boolean {
  try {
    emailSchema.parse(email);
    return true;
  } catch {
    return false;
  }
}

// Validation with error message
export function validateEmail(email: string): { valid: boolean; error?: string } {
  try {
    emailSchema.parse(email);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { valid: false, error: error.errors[0]?.message || "Invalid email" };
    }
    return { valid: false, error: "Invalid email" };
  }
}

// Check for disposable/temporary email domains
const DISPOSABLE_DOMAINS = [
  "tempmail.com", "throwaway.com", "guerrillamail.com", "mailinator.com",
  "10minutemail.com", "yopmail.com", "temp-mail.org", "fakeinbox.com"
];

export function isDisposableEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? DISPOSABLE_DOMAINS.includes(domain) : false;
}

// Format email for display (mask middle part)
export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  
  if (local.length <= 3) {
    return `${local[0]}***@${domain}`;
  }
  
  return `${local.slice(0, 2)}***${local.slice(-1)}@${domain}`;
}
